/*
  URL パラメータの定義についてはこちらを参照
  https://docs.google.com/spreadsheets/d/1WzygvuoHskdmK1BLKSskZR2HnWuT-K4dBeo3k8jV54o/edit#gid=52732880
*/
import config from '@config'
import { encodeGetParams } from '@ota-frontend/core/helpers/url'
import { asNumber, compactObject, csvToArray } from '@ota-frontend/core/utils'
import { SearchCondition } from '@/models'

export const createSearchConditionFromQuery = (query) =>
  new SearchCondition({
    [SearchCondition.PARAMS.SEARCH_TYPE]: query.condition_flg,
    [SearchCondition.PARAMS.DOMESTIC]: query.domestic,
    [SearchCondition.PARAMS.DEPARTURE_PLACE]: asNumber(
      query.departure_place_id
    ),
    [SearchCondition.PARAMS.REGION_OF_DESTINATION1]: asNumber(query.region_id),
    [SearchCondition.PARAMS.COUNTRY_OF_DESTINATION1]: asNumber(
      query.country_id
    ),
    [SearchCondition.PARAMS.CITY_OF_DESTINATION1]: asNumber(query.city_id),
    [SearchCondition.PARAMS.DISTRICT_OF_DESTINATION1]: asNumber(
      query.district_id
    ),
    [SearchCondition.PARAMS.REGION_OF_DESTINATION2]: asNumber(query.region_id2),
    [SearchCondition.PARAMS.COUNTRY_OF_DESTINATION2]: asNumber(
      query.country_id2
    ),
    [SearchCondition.PARAMS.CITY_OF_DESTINATION2]: asNumber(query.city_id2),
    [SearchCondition.PARAMS.DISTRICT_OF_DESTINATION2]: asNumber(
      query.district_id2
    ),
    [SearchCondition.PARAMS.REGION_OF_DESTINATION3]: asNumber(query.region_id3),
    [SearchCondition.PARAMS.COUNTRY_OF_DESTINATION3]: asNumber(
      query.country_id3
    ),
    [SearchCondition.PARAMS.CITY_OF_DESTINATION3]: asNumber(query.city_id3),
    [SearchCondition.PARAMS.DISTRICT_OF_DESTINATION3]: asNumber(
      query.district_id3
    ),
    [SearchCondition.PARAMS.REGION_OF_DESTINATION4]: asNumber(query.region_id4),
    [SearchCondition.PARAMS.COUNTRY_OF_DESTINATION4]: asNumber(
      query.country_id4
    ),
    [SearchCondition.PARAMS.CITY_OF_DESTINATION4]: asNumber(query.city_id4),
    [SearchCondition.PARAMS.DISTRICT_OF_DESTINATION4]: asNumber(
      query.district_id4
    ),
    [SearchCondition.PARAMS.DEPARTURE_MONTH]: query['y-m'],
    [SearchCondition.PARAMS.DEPARTURE_DAY]: query.d,
    [SearchCondition.PARAMS.DURATION_FROM]: asNumber(query.duration_from),
    [SearchCondition.PARAMS.DURATION_TO]: asNumber(query.duration_to),
    [SearchCondition.PARAMS.NUMBER_OF_PASSENGERS]: asNumber(
      query.passenger_count
    ),
    [SearchCondition.PARAMS.CAMPAIGNS]: csvToArray(query.campaign_ids),
    [SearchCondition.PARAMS.KEYWORD]: query.keyword,
    [SearchCondition.PARAMS.PRICE_FROM]: asNumber(query.price_from),
    [SearchCondition.PARAMS.PRICE_TO]: asNumber(query.price_to),
    [SearchCondition.PARAMS.INTENTIONS]: csvToArray(query.intention_ids),
    [SearchCondition.PARAMS.MIN_HOTEL_RANK]: asNumber(
      query.hotel_important_level
    ),
    [SearchCondition.PARAMS.HOTELS_OF_DESTINATION1]: csvToArray(
      query.hotel_id_set || query.hotel_id1
    ), // hotel_id_set['',1,2,3] は 2021/11 刷新以前の旧アプリ PC 版でのみ設定されていたパラメータで、互換性のためだけに変換を定義している. 現在は使われていないので、十分な時間が経ったら削除してしまった方が良い
    [SearchCondition.PARAMS.HOTELS_OF_DESTINATION2]: csvToArray(
      query.hotel_id_set1 || query.hotel_id2
    ),
    [SearchCondition.PARAMS.HOTELS_OF_DESTINATION3]: csvToArray(
      query.hotel_id_set2 || query.hotel_id3
    ),
    [SearchCondition.PARAMS.HOTELS_OF_DESTINATION4]: csvToArray(
      query.hotel_id_set3 || query.hotel_id4
    ),
    [SearchCondition.PARAMS.STOPOVER]: query.stopover_flg,
    [SearchCondition.PARAMS.TRAIN]: query.train,
    [SearchCondition.PARAMS.AIRLINES]: csvToArray(query.airline_id),
    [SearchCondition.PARAMS.SEAT_CODE]: query.seat_code,
    [SearchCondition.PARAMS.DEPARTURE_PLACE_TIME_CODE]:
      query.departure_place_time_code,
    [SearchCondition.PARAMS.DESTINATION_ARRIVAL_TIME_CODE]:
      query.destination_arrival_time_code,
    [SearchCondition.PARAMS.DESTINATION_TIME_CODE]: query.destination_time_code,
    [SearchCondition.PARAMS.DEPARTURE_PLACE_ARRIVAL_TIME_CODE]:
      query.departure_place_arrival_time_code,
    [SearchCondition.PARAMS.ORDER]: query.order,
    [SearchCondition.PARAMS.CODE]: query.code,
  })

export const buildQueryFromSearchCondition = (condition, passiveParameters) =>
  ((destinations) =>
    compactObject({
      condition_flg: condition.searchType,
      domestic: condition.rawDomestic,
      departure_place_id: condition.departurePlaceId,
      region_id: destinations[0].regionId,
      country_id: destinations[0].countryId,
      city_id: destinations[0].cityId,
      district_id: destinations[0].districtId,
      region_id2: destinations[1].regionId,
      country_id2: destinations[1].countryId,
      city_id2: destinations[1].cityId,
      district_id2: destinations[1].districtId,
      region_id3: destinations[2].regionId,
      country_id3: destinations[2].countryId,
      city_id3: destinations[2].cityId,
      district_id3: destinations[2].districtId,
      region_id4: destinations[3].regionId,
      country_id4: destinations[3].countryId,
      city_id4: destinations[3].cityId,
      district_id4: destinations[3].districtId,
      'y-m': (condition.departureDateAsIsoFormat || '').slice(0, 7),
      d: (condition.departureDateAsIsoFormat || '').slice(8, 10),
      duration_from: condition.durationFrom,
      duration_to: condition.durationTo,
      passenger_count: condition.numberOfPassengers,
      campaign_ids: condition.campaignIds.join(','),
      keyword: condition.keyword,
      price_from: condition.priceFrom,
      price_to: condition.priceTo,
      intention_ids: condition.groupedIntentionIds
        .map((idsOfName) => idsOfName.join('-'))
        .join(','),
      hotel_important_level: condition.minHotelRank,
      hotel_id1: destinations[0].hotelIds.join(','),
      hotel_id2: destinations[1].hotelIds.join(','),
      hotel_id3: destinations[2].hotelIds.join(','),
      hotel_id4: destinations[3].hotelIds.join(','),
      stopover_flg: condition.preferDirectFlight ? '0' : undefined,
      train: condition.trainIds[0],
      airline_id: condition.airlineIds.join(','),
      seat_code: condition.seatCode,
      departure_place_time_code: condition.departurePlaceTimeCode,
      destination_arrival_time_code: condition.destinationArrivalTimeCode,
      destination_time_code: condition.destinationTimeCode,
      departure_place_arrival_time_code:
        condition.departurePlaceArrivalTimeCode,
      order: condition.order,
      code: condition.code,
      ...passiveParameters,
    }))(condition.destinations)

// 検索条件モデルを使うべき
export const makeDetailPageUrl = (
  currentQuery,
  uriForOnline,
  uriForOffline,
  path
) => (saleType) => (tourCode) => {
  const isSeo =
    path.startsWith('/japan/tour/') && !path.startsWith('/japan/tour/lp')
  let url = saleType === 'online' ? uriForOnline : `${uriForOffline}${tourCode}`
  if (isSeo) {
    url = `/japan/tour/${tourCode.toLowerCase()}`
  }

  // sale type 毎のURLパラメータ
  const queryForTheSaleType = (() => {
    switch (saleType) {
      case 'online':
        return isSeo ? {} : { ...currentQuery, goods_cd: tourCode }
      case 'offline': {
        const q = {} // 実質 const ではないが eslint の prefer-const に従っている
        if (currentQuery['y-m']) {
          q['y-m'] = currentQuery['y-m']
          if (currentQuery.d) {
            q.d = currentQuery.d
          }
        }
        if (currentQuery.passenger_count) {
          q.passenger_count = currentQuery.passenger_count
        }
        return q
      }
      default:
        return {}
    }
  })()

  // sale type によらず特別に引き継ぐパラメータを含める
  const query = {
    ...queryForTheSaleType,
    ...compactObject({
      research: currentQuery.research, // 再検索後かどうか
    }),
  }

  return Object.keys(query).length > 0
    ? `${url}?${encodeGetParams(query)}`
    : url
}

export const makeSearchAgainUrl = (searchCondition) => {
  const withoutExcessiveDestinations =
    searchCondition.searchType === SearchCondition.SEARCH_TYPES.EITHER
      ? searchCondition.omitDestinationUpToNth(1) // 第２目的地以降は外す
      : searchCondition
  const withLpIntentions = withoutExcessiveDestinations.filterIntentionsByFacets(
    searchCondition.facets.popularIntentions
  )

  return `${config.pageUrl.tourTop}?${encodeGetParams(
    buildQueryFromSearchCondition(withLpIntentions)
  )}`
}
